import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Material UI Icons. */
import LeaderboardIcon from '@mui/icons-material/Leaderboard';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { PageHeader } from 'shared/ui/HeaderIcon';
import { callAPI } from 'shared/api/callApi';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import CustomSnackbar from 'shared/ui/CustomSnackbar';

/** Analytics Components. */
import VolumeChart from './components/VolumeChart';
import VolumeChanges from './components/VolumeChanges';

export default function Analytics() {
    /** Destructuring values from useAuth0 hook. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** State for the line chart and volume chart data. */
    const [lineChart, setLineChart] = useState({ data: [], label: [] });
    /** State for the bar chart and volume chart data. */
    const [barChart, setBarChart] = useState({ data: [], label: [] });
    /** State for the trade partner list and volume changes. */
    const [tradePartnerList, setTradePartnerList] = useState([]);
    /** State for the volume changes in main and sub categories. */
    const [volumeChanges, setVolumeChanges] = useState({ master: [], sub: [] });
    /** State to manage loading state for trade partner data. */
    const [loadingTradePartner, setLoadingTradePartner] = useState(false);

    /** Named website title. */
    useEffect(() => {
        document.title = 'Analytics | Operation System';
    }, []);

    /** Call GET request when the page is first rendered.
     *  setTradePartnerList: state setting for client, shipper, and consignee
     *  setVolumeChanges: state setting for main and sub volume changes
     */
    useEffect(() => {
        callAPI({
            method: 'get',
            module: 'analytics',
            component: 'tradePartner',
            getAccessTokenSilently,
            email: user.email,
            onSuccess: (resp) => {
                setTradePartnerList({
                    client: resp.data.client,
                    shipper: resp.data.shipper,
                    consignee: resp.data.consignee,
                });
                setVolumeChanges({
                    master: resp.data.masterVolumeData,
                    sub: resp.data.subVolumeData,
                });
            },
            onError: () =>
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Someting went wrong. Please try again.',
                        severity: 'error',
                    })
                ),
            onFinal: () => {
                setLoadingTradePartner(true);
            },
        });
    }, [getAccessTokenSilently]);

    return (
        <>
            <CustomSnackbar />
            <Paper
                sx={{
                    ...customized_style.itemPaper,
                    height: { xs: 'auto', lg: customized_style.itemPaper.height },
                    minHeight: { xs: '100vh', lg: customized_style.itemPaper.minHeight },
                }}
            >
                {!loadingTradePartner ? (
                    <CustomSkeleton2 />
                ) : (
                    <>
                        <PageHeader title={'ANALYTICS'} icon={LeaderboardIcon} sx={{ mb: 1 }} />
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, lg: 6 }}>
                                <VolumeChart
                                    lineChart={lineChart}
                                    barChart={barChart}
                                    tradePartnerList={tradePartnerList}
                                    setLineChart={setLineChart}
                                    setBarChart={setBarChart}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, lg: 6 }}>
                                <VolumeChanges master={volumeChanges.master} sub={volumeChanges.sub} />
                            </Grid>
                        </Grid>
                    </>
                )}
            </Paper>
        </>
    );
}
